import * as React from 'react';
import { Extension, InputRule, type JSONContent } from '@tiptap/core';
import { Highlight } from '@tiptap/extension-highlight';
import { Color, FontFamily, FontSize, TextStyle } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extensions';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import {
  ALargeSmall,
  Bold,
  Highlighter,
  Italic,
  List,
  Palette,
  Strikethrough,
  Type,
  Underline,
} from 'lucide-react';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/cargofl-ui';
import type { GoalUpdate } from '@/lib/goals-api';
import { cn } from '@/lib/utils';

/**
 * Formatting for free-text notes, stored as plain text and drawn as React elements -
 * never as HTML, so nothing typed into a note can become markup.
 *
 * Two inline syntaxes are read. The editor writes runs, `{{b,i,c=blue|text}}`, one per
 * stretch of identically formatted text, so nesting never needs parsing. Notes saved
 * before the editor existed use markdown-ish markers (`**bold**`, `==highlight==`) and
 * still render. A note with neither renders exactly as typed.
 *
 * Colour comes only from the categorical chart tokens: no red or amber, which mean
 * "late" and "problem" everywhere else on these screens.
 */
type Swatches = Record<string, { label: string; css: string }>;

const COLORS: Swatches = {
  grey: { label: 'Grey', css: 'hsl(var(--muted-foreground))' },
  teal: { label: 'Teal', css: 'hsl(var(--chart-1))' },
  blue: { label: 'Blue', css: 'hsl(var(--chart-4))' },
  purple: { label: 'Purple', css: 'hsl(var(--chart-2))' },
  pink: { label: 'Pink', css: 'hsl(var(--chart-5))' },
};
const SIZES: Swatches = {
  sm: { label: 'Small', css: '12px' },
  lg: { label: 'Large', css: '16px' },
  xl: { label: 'Huge', css: '20px' },
};
const FONTS: Swatches = {
  serif: { label: 'Serif', css: 'Georgia, "Times New Roman", serif' },
  mono: { label: 'Mono', css: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
};

const MARK_CLASS = 'rounded-sm bg-primary/15 px-0.5 text-foreground';

// --- Reading ---------------------------------------------------------------

const INLINE: { open: string; render: (s: string, k: number) => React.ReactNode }[] = [
  { open: '**', render: (s, k) => <strong key={k}>{s}</strong> },
  { open: '__', render: (s, k) => <u key={k}>{s}</u> },
  { open: '~~', render: (s, k) => <s key={k}>{s}</s> },
  {
    open: '==',
    render: (s, k) => (
      <mark key={k} className={MARK_CLASS}>
        {s}
      </mark>
    ),
  },
  { open: '*', render: (s, k) => <em key={k}>{s}</em> },
];

// Longest markers first, so `**x**` is bold and not an italic `*` around `*x*`.
// Markdown's rule: the marker must hug the text, so `5 * 3 = 15 * 2` stays arithmetic.
const MARKER =
  /(\*\*(?=\S)[^*\n]+?(?<=\S)\*\*|__(?=\S)[^_\n]+?(?<=\S)__|~~(?=\S)[^~\n]+?(?<=\S)~~|==(?=\S)[^=\n]+?(?<=\S)==|\*(?=\S)[^*\n]+?(?<=\S)\*)/g;

/** The older marker syntax. No nesting: notes from before the editor never had any. */
function markerNodes(line: string): React.ReactNode[] {
  // split() with a capture group puts the matches at odd indices; even ones are plain text.
  return line.split(MARKER).map((part, i) => {
    const fmt = i % 2 ? INLINE.find((f) => part.startsWith(f.open) && part.endsWith(f.open)) : undefined;
    return fmt ? fmt.render(part.slice(fmt.open.length, -fmt.open.length), i) : part;
  });
}

const RUN = /\{\{([a-z=,]*)\|(.*?)\}\}/g;

interface Fmt {
  b?: boolean;
  i?: boolean;
  u?: boolean;
  s?: boolean;
  h?: boolean;
  c?: string;
  z?: string;
  f?: string;
}

/** Unknown flags and swatch names are dropped, so a hand-edited run renders as plain text. */
function parseFlags(flags: string): Fmt {
  const fmt: Fmt = {};
  for (const flag of flags.split(',')) {
    const [k = '', v] = flag.split('=');
    if (v === undefined && ['b', 'i', 'u', 's', 'h'].includes(k)) fmt[k as 'b'] = true;
    if (k === 'c' && v && Object.hasOwn(COLORS, v)) fmt.c = v;
    if (k === 'z' && v && Object.hasOwn(SIZES, v)) fmt.z = v;
    if (k === 'f' && v && Object.hasOwn(FONTS, v)) fmt.f = v;
  }
  return fmt;
}

function renderRun(text: string, f: Fmt, key: number): React.ReactNode {
  let node: React.ReactNode = text;
  if (f.h) node = <mark className={MARK_CLASS}>{node}</mark>;
  if (f.s) node = <s>{node}</s>;
  if (f.u) node = <u>{node}</u>;
  if (f.i) node = <em>{node}</em>;
  if (f.b) node = <strong>{node}</strong>;
  if (f.c || f.z || f.f) {
    node = (
      <span
        style={{
          color: f.c ? COLORS[f.c]?.css : undefined,
          fontSize: f.z ? SIZES[f.z]?.css : undefined,
          fontFamily: f.f ? FONTS[f.f]?.css : undefined,
        }}
      >
        {node}
      </span>
    );
  }
  return <React.Fragment key={key}>{node}</React.Fragment>;
}

export function inlineNodes(line: string): React.ReactNode[] {
  const runs = [...line.matchAll(RUN)];
  if (runs.length === 0) return markerNodes(line);

  const out: React.ReactNode[] = [];
  let last = 0;
  runs.forEach((m, i) => {
    const at = m.index ?? 0;
    if (at > last) out.push(<React.Fragment key={`t${i}`}>{markerNodes(line.slice(last, at))}</React.Fragment>);
    out.push(renderRun(m[2] ?? '', parseFlags(m[1] ?? ''), i));
    last = at + m[0].length;
  });
  if (last < line.length) out.push(<React.Fragment key="tail">{markerNodes(line.slice(last))}</React.Fragment>);
  return out;
}

export function RichNote({ text, className }: { text: string; className?: string }) {
  return (
    <div className={className}>
      {text.split('\n').map((line, i) =>
        line.startsWith('- ') ? (
          <div key={i} className="flex gap-1.5">
            <span aria-hidden>•</span>
            <span>{inlineNodes(line.slice(2))}</span>
          </div>
        ) : (
          // An empty line keeps its height, the way whitespace-pre-wrap drew it.
          <div key={i} className="min-h-[1lh] whitespace-pre-wrap">
            {inlineNodes(line)}
          </div>
        ),
      )}
    </div>
  );
}

/** One logged progress update - My goals and the assessment draw it the same way. */
export function ProgressEntry({ update: u }: { update: GoalUpdate }) {
  return (
    <div className="border-l-2 border-border pl-3 text-sm">
      <RichNote text={u.note} />
      <p className="mt-0.5 text-xs text-muted-foreground">
        {u.authorName} · {new Date(u.createdAt).toLocaleDateString()}
        {u.progressPct !== null ? ` · ${u.progressPct}%` : ''}
      </p>
    </div>
  );
}

// --- Writing ---------------------------------------------------------------

/** Literal markers or braces in unformatted text: wrapped in an empty run so they stay literal. */
const NEEDS_ESCAPE = /[*_~=]|\{\{/;

const swatchKey = (swatches: Swatches, css: unknown) =>
  Object.keys(swatches).find((k) => swatches[k]?.css === css);

/** Pasted colours and sizes outside the swatches are dropped here, not stored. */
function flagsOf(marks: NonNullable<JSONContent['marks']>): string {
  const on = (type: string) => marks.some((m) => m.type === type);
  const style = marks.find((m) => m.type === 'textStyle')?.attrs ?? {};
  const c = swatchKey(COLORS, style.color);
  const z = swatchKey(SIZES, style.fontSize);
  const f = swatchKey(FONTS, style.fontFamily);
  return [
    on('bold') && 'b',
    on('italic') && 'i',
    on('underline') && 'u',
    on('strike') && 's',
    on('highlight') && 'h',
    c && `c=${c}`,
    z && `z=${z}`,
    f && `f=${f}`,
  ]
    .filter(Boolean)
    .join(',');
}

function lineText(content: JSONContent[]): string {
  return content
    .map((n) => {
      if (n.type === 'hardBreak') return '\n';
      // A zero-width space keeps a typed `}}` from ending its run early.
      const text = (n.text ?? '').replaceAll('}}', '}​}');
      const flags = flagsOf(n.marks ?? []);
      return flags || NEEDS_ESCAPE.test(text) ? `{{${flags}|${text}}}` : text;
    })
    .join('');
}

const capitalize = (s: string) => s.replace(/^\p{Ll}/u, (c) => c.toUpperCase());

/** The editor's document as the stored note: one line per paragraph, `- ` per bullet. */
export function serializeDoc(doc: JSONContent): string {
  const lines: string[] = [];
  const walk = (node: JSONContent, bullet: boolean) => {
    if (node.type === 'paragraph') {
      const [first, ...rest] = node.content ?? [];
      // Every line starts with a capital, including text pasted in lowercase.
      const content = first?.text
        ? [{ ...first, text: capitalize(first.text) }, ...rest]
        : (node.content ?? []);
      lines.push((bullet ? '- ' : '') + lineText(content));
      return;
    }
    for (const child of node.content ?? []) walk(child, bullet || node.type === 'bulletList');
  };
  walk(doc, false);
  return lines.join('\n');
}

function marksOf(f: Fmt): NonNullable<JSONContent['marks']> {
  const marks: NonNullable<JSONContent['marks']> = [];
  if (f.b) marks.push({ type: 'bold' });
  if (f.i) marks.push({ type: 'italic' });
  if (f.u) marks.push({ type: 'underline' });
  if (f.s) marks.push({ type: 'strike' });
  if (f.h) marks.push({ type: 'highlight' });
  if (f.c || f.z || f.f) {
    marks.push({
      type: 'textStyle',
      attrs: {
        color: (f.c && COLORS[f.c]?.css) || null,
        fontSize: (f.z && SIZES[f.z]?.css) || null,
        fontFamily: (f.f && FONTS[f.f]?.css) || null,
      },
    });
  }
  return marks;
}

const unescapeBraces = (s: string) => s.replaceAll('}​}', '}}');

function textNodes(line: string): JSONContent[] {
  const nodes: JSONContent[] = [];
  const plain = (s: string) => {
    if (s) nodes.push({ type: 'text', text: unescapeBraces(s) });
  };
  let last = 0;
  for (const m of line.matchAll(RUN)) {
    const at = m.index ?? 0;
    plain(line.slice(last, at));
    const text = unescapeBraces(m[2] ?? '');
    if (text) nodes.push({ type: 'text', text, marks: marksOf(parseFlags(m[1] ?? '')) });
    last = at + m[0].length;
  }
  plain(line.slice(last));
  return nodes;
}

/**
 * A stored note back into the editor's document - the inverse of serializeDoc, for
 * reopening a draft with its formatting. Reads the runs the editor writes; old marker
 * notes are never reopened, since posted updates cannot be edited.
 */
export function parseNote(text: string): JSONContent {
  const content: JSONContent[] = [];
  let list: JSONContent[] | null = null;
  for (const line of text.split('\n')) {
    const bullet = line.startsWith('- ');
    const nodes = textNodes(bullet ? line.slice(2) : line);
    const paragraph: JSONContent = nodes.length ? { type: 'paragraph', content: nodes } : { type: 'paragraph' };
    if (!bullet) {
      list = null;
      content.push(paragraph);
      continue;
    }
    if (!list) {
      list = [];
      content.push({ type: 'bulletList', content: list });
    }
    list.push({ type: 'listItem', content: [paragraph] });
  }
  return { type: 'doc', content };
}

/** Typing the first letter of a paragraph or bullet capitalises it; Backspace undoes that. */
const CapitalizeFirst = Extension.create({
  name: 'capitalizeFirst',
  addInputRules() {
    return [
      new InputRule({
        find: /^\p{Ll}$/u,
        handler: ({ state, range, match }) => {
          state.tr.insertText(match[0].toUpperCase(), range.from, range.to);
        },
      }),
    ];
  },
});

const TOGGLES: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active: string;
  run: (e: Editor) => void;
}[] = [
  { icon: Bold, label: 'Bold (Ctrl+B)', active: 'bold', run: (e) => e.chain().focus().toggleBold().run() },
  { icon: Italic, label: 'Italic (Ctrl+I)', active: 'italic', run: (e) => e.chain().focus().toggleItalic().run() },
  {
    icon: Underline,
    label: 'Underline (Ctrl+U)',
    active: 'underline',
    run: (e) => e.chain().focus().toggleUnderline().run(),
  },
  { icon: Strikethrough, label: 'Strikethrough', active: 'strike', run: (e) => e.chain().focus().toggleStrike().run() },
  { icon: Highlighter, label: 'Highlight', active: 'highlight', run: (e) => e.chain().focus().toggleHighlight().run() },
  { icon: List, label: 'Bulleted list', active: 'bulletList', run: (e) => e.chain().focus().toggleBulletList().run() },
];

const STYLE_MENUS: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  plain: string;
  swatches: Swatches;
  preview: (css: string) => React.CSSProperties;
  set: (e: Editor, css: string) => void;
  unset: (e: Editor) => void;
}[] = [
  {
    icon: Palette,
    label: 'Font colour',
    plain: 'Default',
    swatches: COLORS,
    preview: (css) => ({ color: css }),
    set: (e, css) => e.chain().focus().setColor(css).run(),
    unset: (e) => e.chain().focus().unsetColor().run(),
  },
  {
    icon: ALargeSmall,
    label: 'Font size',
    plain: 'Normal',
    swatches: SIZES,
    preview: (css) => ({ fontSize: css }),
    set: (e, css) => e.chain().focus().setFontSize(css).run(),
    unset: (e) => e.chain().focus().unsetFontSize().run(),
  },
  {
    icon: Type,
    label: 'Font',
    plain: 'Default',
    swatches: FONTS,
    preview: (css) => ({ fontFamily: css }),
    set: (e, css) => e.chain().focus().setFontFamily(css).run(),
    unset: (e) => e.chain().focus().unsetFontFamily().run(),
  },
];

const TOOL_CLASS = 'size-7 text-muted-foreground hover:text-foreground';

/**
 * A note editor that shows formatting as it is typed - bold looks bold, no markers.
 * Ctrl/Cmd+B/I/U work, and `**x**` or `- ` typed by hand still turns into the format.
 *
 * Opens on `value` (a reopened draft, or empty), then is watched only for being cleared
 * after a post. Every change comes back through `onChange` already in the stored form.
 */
export function RichNoteEditor({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (note: string) => void;
  placeholder: string;
  label: string;
}) {
  // TipTap binds onUpdate once, at creation; the ref keeps it calling the latest onChange.
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  });

  const editor = useEditor({
    content: value ? parseNote(value) : '',
    extensions: [
      // Only what the stored form can hold, so what is on screen is what gets saved.
      StarterKit.configure({
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        orderedList: false,
        link: false,
        bulletList: { HTMLAttributes: { class: 'list-disc pl-5' } },
      }),
      TextStyle,
      Color,
      FontSize,
      FontFamily,
      Highlight.configure({ HTMLAttributes: { class: MARK_CLASS } }),
      Placeholder.configure({ placeholder }),
      CapitalizeFirst,
    ],
    editorProps: {
      attributes: {
        class: 'min-h-14 px-3 py-2 text-sm focus:outline-none',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': label,
      },
      // Pasted colours and sizes would show here and then vanish on save; drop them up front.
      transformPastedHTML: (html) => html.replace(/\sstyle="[^"]*"/gi, ''),
    },
    onUpdate: ({ editor: e }) => onChangeRef.current(serializeDoc(e.getJSON())),
    // The toolbar's pressed states follow the cursor.
    shouldRerenderOnTransaction: true,
  });

  React.useEffect(() => {
    if (value === '' && editor && !editor.isEmpty) editor.commands.clearContent(false);
  }, [value, editor]);

  return (
    <>
      <div
        role="toolbar"
        aria-label="Formatting"
        className="flex flex-wrap items-center gap-0.5 border-b border-input bg-muted/30 px-1.5 py-1"
      >
        {TOGGLES.map((t) => {
          const pressed = editor?.isActive(t.active) ?? false;
          return (
            <Button
              key={t.label}
              type="button"
              variant="ghost"
              size="icon"
              className={cn(TOOL_CLASS, pressed && 'bg-accent text-foreground')}
              aria-label={t.label}
              aria-pressed={pressed}
              disabled={!editor}
              // Keeps the editor's selection: a click would otherwise blur it first.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor && t.run(editor)}
            >
              <t.icon className="size-4" />
            </Button>
          );
        })}

        <span aria-hidden className="mx-1 h-4 w-px bg-border" />

        {STYLE_MENUS.map((m) => (
          <DropdownMenu key={m.label}>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon" className={TOOL_CLASS} aria-label={m.label} disabled={!editor}>
                <m.icon className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            {/* Focus goes back to the text, not the trigger, so typing carries on. */}
            <DropdownMenuContent align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
              <DropdownMenuItem onSelect={() => editor && m.unset(editor)}>{m.plain}</DropdownMenuItem>
              {Object.entries(m.swatches).map(([key, s]) => (
                <DropdownMenuItem key={key} onSelect={() => editor && m.set(editor, s.css)}>
                  <span style={m.preview(s.css)}>{s.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ))}
      </div>
      <EditorContent editor={editor} />
    </>
  );
}
